import { useEffect, useRef, useState } from 'react';

/**
 * Syncs a hidden <audio> track to a <video> element for blast music.
 * When musicUrl is present the video is kept muted and the track plays over it.
 * Pass `externalMuted` to drive the track's mute from a shared (feed-wide) toggle.
 */
export default function useMusicSync(videoRef, musicUrl, startMs = 0, externalMuted) {
  const audioRef = useRef(null);
  const [localMuted, setMusicMuted] = useState(false);
  const musicMuted = externalMuted ?? localMuted;
  const hasMusic = !!musicUrl;

  useEffect(() => {
    if (!hasMusic) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;

    const audio = new Audio(musicUrl);
    audio.loop = false;
    audio.muted = musicMuted;
    audioRef.current = audio;

    const startSec = (startMs || 0) / 1000;
    let lastTime = 0;

    const onPlay = () => {
      video.muted = true; // music replaces original audio
      if (audio.currentTime < startSec) audio.currentTime = startSec;
      audio.play().catch(() => {});
    };
    const onPause = () => audio.pause();
    const onTimeUpdate = () => {
      // Detect the video loop wrap and reset the track to its start point.
      if (video.currentTime < lastTime - 0.3) audio.currentTime = startSec;
      lastTime = video.currentTime;
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    if (!video.paused) onPlay();

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      audio.pause();
      audioRef.current = null;
    };
  }, [hasMusic, musicUrl, startMs]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = musicMuted;
  }, [musicMuted]);

  const toggleMusicMuted = () => setMusicMuted((m) => !m);

  return { musicMuted, toggleMusicMuted, hasMusic };
}
